/**
 * SignupPage - HI-SI OAuth signup page
 * HISI-FE-AUTH-01: Updated for cookie-based authentication
 */

import { motion } from 'framer-motion';
import { Brain, ArrowLeft } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';

import AtmosphericBackground from '@/components/futuristic/AtmosphericBackground';
import { supabase } from '@/lib/supabase';
import { cn } from '@/utils/cn';

export default function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get redirect URL from params
  const redirectTo = searchParams.get('redirect_to') || '/dashboard';

  // Handle Google OAuth signup - Browser-initiated PKCE flow
  // CRITICAL: Browser must control PKCE verifier generation/storage
  const handleGoogleSignup = async () => {
    try {
      // Generate client-side trace ID for debugging correlation
      const clientTraceId = `signup-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      console.group('🔒 OAuth Signup: Browser-initiated PKCE flow');
      console.log('🆔 Client Trace ID:', clientTraceId);
      console.log('🎯 Redirect target:', redirectTo);
      console.log('🌐 Current origin:', window.location.origin);

      // Validate redirect domain for security
      const currentDomain = window.location.hostname;
      const allowedDomains = ['hi-si.ai', 'staging.hi-si.ai', 'localhost', '127.0.0.1'];

      if (!allowedDomains.some(domain => currentDomain.includes(domain))) {
        throw new Error('Invalid redirect domain');
      }

      // Browser-initiated OAuth with PKCE (critical for security)
      // The browser will generate and store the PKCE code verifier
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          // Callback URL must match Supabase configuration
          redirectTo: `https://hi-si.ai/auth/callback?redirect_to=${encodeURIComponent(redirectTo)}`,
          // Additional query parameters for branding/tracking
          queryParams: {
            brand: 'hi-si',
            utm_source: 'signup',
            utm_medium: 'oauth',
            utm_campaign: 'hisi_signup',
            trace_id: clientTraceId
          }
        }
      });

      if (error) {
        console.error('❌ OAuth signup initiation failed:', error);
        console.groupEnd();
        throw error;
      }

      console.log('✅ OAuth signup flow initiated successfully');
      console.log('🔄 Redirecting to Google OAuth...');
      console.groupEnd();

      // Note: Browser automatically redirects to Google OAuth
      // After user consent, Google will redirect back to /auth/callback with ?code=...
      // The callback page will POST the code to /api/auth/callback for cookie exchange

    } catch (error) {
      console.error('OAuth signup failed:', error);
      // Could add toast notification here for user feedback
    }
  };

  // HISI-FE-AUTH-01: Single-brand HI-SI app

  return (
    <>
      <Helmet>
        <title>Sign Up - HI-SI | High Signal Intelligence</title>
        <meta name="description" content="Create your HI-SI account and start creating professional videos with our AI-powered pipeline." />
      </Helmet>

      <div className="min-h-screen bg-black text-white relative flex items-center justify-center">
        {/* Background */}
        <div className="absolute inset-0">
          <AtmosphericBackground />
        </div>

        {/* Back to Landing */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/')}
          className="absolute top-8 left-8 z-20 flex items-center gap-2 text-white/70 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Home</span>
        </motion.button>

        {/* Signup Form */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 w-full max-w-md px-6"
        >
          {/* Brand Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-cyan-500/20 border border-purple-500/30 flex items-center justify-center">
                <Brain className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <div className="text-2xl font-light">
                  <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                    HI-SI
                  </span>
                </div>
                <div className="text-xs text-white/50 font-mono uppercase tracking-wider">
                  High Signal Intelligence
                </div>
              </div>
            </div>
            
            <h1 className="text-3xl font-light text-white mb-2">
              Create Your Account
            </h1>
            <p className="text-white/70">
              Start creating professional videos with AI
            </p>
          </div>

          {/* Signup Card */}
          <div className="bg-black/50 backdrop-blur-xl rounded-2xl border border-white/10 p-8">
            
            {/* Google OAuth Button */}
            <motion.button
              onClick={handleGoogleSignup}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={cn(
                "w-full py-4 px-6 rounded-xl font-medium transition-all duration-300",
                "bg-white text-black hover:bg-white/90",
                "flex items-center justify-center gap-3",
                "shadow-lg hover:shadow-xl"
              )}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign up with Google
            </motion.button>

            {/* Terms and Privacy */}
            <p className="text-xs text-white/60 text-center mt-6 leading-relaxed">
              By signing up, you agree to our{' '}
              <a href="/terms" target="_blank" className="text-cyan-400 hover:text-cyan-300 underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" target="_blank" className="text-cyan-400 hover:text-cyan-300 underline">
                Privacy Policy
              </a>
            </p>
          </div>

          {/* Login Link */}
          <div className="text-center mt-8">
            <p className="text-white/70 text-sm">
              Already have an account?{' '}
              <button
                onClick={() => navigate(`/login?redirect_to=${encodeURIComponent(redirectTo)}`)}
                className="text-cyan-400 hover:text-cyan-300 font-medium underline"
              >
                Log in
              </button>
            </p>
          </div>

          {/* Feature Highlight */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-12 text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20">
              <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
              <span className="text-xs text-white/80">
                7-day free trial • No credit card required
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </>
  );
}